/**
 * Admin API functions for dashboard data and order management.
 */

import type {
  DashboardData,
  PaginatedOrders,
  AdminOrderDetail,
  WeightCaptureResult,
  OrderFilters,
  WeightApprovalSummary,
  WeightApprovalResult,
  AwaitingWeightOrder,
  MorningBriefData,
  AdminNotification,
  SearchResult,
} from '@provendoro/shared/types'
import { apiFetch, buildQuery, getActiveBrand } from './api-client.js'

export async function fetchDashboardData(): Promise<DashboardData | null> {
  const brandId = getActiveBrand()
  const query = buildQuery({ brandId })
  const res = await apiFetch<DashboardData>(`/api/v1/admin/dashboard${query}`)
  return res.data ?? null
}

export async function fetchOrders(filters: Partial<OrderFilters> = {}): Promise<PaginatedOrders | null> {
  const brandId = getActiveBrand()
  const query = buildQuery({
    channel: filters.channel,
    status: filters.status,
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
    brandId: brandId ?? filters.brandId,
    search: filters.search,
    page: filters.page,
    limit: filters.limit,
  })
  const res = await apiFetch<PaginatedOrders>(`/api/v1/admin/orders${query}`)
  return res.data ?? null
}

export async function fetchOrderDetail(orderId: string): Promise<AdminOrderDetail | null> {
  const res = await apiFetch<AdminOrderDetail>(`/api/v1/admin/orders/${orderId}`)
  return res.data ?? null
}

export async function confirmOrder(orderId: string) {
  return apiFetch<{ status: string; version: number }>(`/api/v1/admin/orders/${orderId}/confirm`, { method: 'PUT' })
}

export async function prepareOrder(orderId: string) {
  return apiFetch<{ status: string; version: number }>(`/api/v1/admin/orders/${orderId}/prepare`, { method: 'PUT' })
}

export async function weightAdjustOrder(orderId: string, items: Array<{ itemId: string; actualWeight: number }>) {
  return apiFetch<WeightCaptureResult>(`/api/v1/admin/orders/${orderId}/weight-adjust`, {
    method: 'PUT',
    body: JSON.stringify({ items }),
  })
}

export async function markReady(orderId: string) {
  return apiFetch<{ status: string; version: number }>(`/api/v1/admin/orders/${orderId}/ready`, { method: 'PUT' })
}

export async function markPickedUp(orderId: string) {
  return apiFetch<{ status: string; version: number }>(`/api/v1/admin/orders/${orderId}/picked-up`, { method: 'PUT' })
}

export async function markNoShow(orderId: string) {
  return apiFetch<{ status: string; version: number }>(`/api/v1/admin/orders/${orderId}/no-show`, { method: 'PUT' })
}

export async function fetchBrands() {
  const res = await apiFetch<Array<{ id: string; name: string; slug: string }>>('/api/v1/brands')
  return res.data ?? []
}

// ── Catch Weight API ──

export async function fetchOrdersAwaitingWeight(): Promise<AwaitingWeightOrder[]> {
  const res = await apiFetch<{ orders: AwaitingWeightOrder[] }>('/api/v1/admin/orders/awaiting-weight')
  return res.data?.orders ?? []
}

export async function fetchWeightSummary(orderId: string) {
  const res = await apiFetch<WeightApprovalSummary>(`/api/v1/admin/orders/${orderId}/weight-summary`)
  return res.data ?? null
}

export async function enterLineItemWeight(orderId: string, lineItemId: string, actualWeightOz: number) {
  return apiFetch<{ success: boolean; lineItemId: string; actualWeightOz: number }>(
    `/api/v1/admin/orders/${orderId}/line-items/${lineItemId}/weight`,
    {
      method: 'PATCH',
      body: JSON.stringify({ actualWeightOz }),
    },
  )
}

export async function approveOrderWeight(orderId: string): Promise<WeightApprovalResult | null> {
  const res = await apiFetch<WeightApprovalResult>(`/api/v1/admin/orders/${orderId}/approve-weight`, {
    method: 'POST',
  })
  return res.data ?? null
}

export async function disputeLineItemWeight(
  orderId: string,
  lineItemId: string,
  correctedWeightOz: number,
  reason: string,
) {
  return apiFetch<{
    lineItemId: string
    originalWeightOz: number
    correctedWeightOz: number
    refundAmountCents: number
    reason: string
  }>(`/api/v1/admin/orders/${orderId}/line-items/${lineItemId}/dispute-weight`, {
    method: 'POST',
    body: JSON.stringify({ correctedWeightOz, reason }),
  })
}

export interface AnalyticsParams {
  start: string
  end: string
  period: 'daily' | 'weekly' | 'monthly'
}

export async function fetchAnalytics(params: AnalyticsParams) {
  const brandId = getActiveBrand()
  const query = buildQuery({
    start: params.start,
    end: params.end,
    period: params.period,
    brandId,
  })
  const res = await apiFetch<import('@provendoro/shared/types').AnalyticsData>(
    `/api/v1/admin/analytics${query}`,
  )
  return res.data ?? null
}

// ── Fulfillment Types ──

export interface AllergenConflict {
  productId: string
  productName: string
  allergen: string
  customerAllergen: string
}

export interface PickListLine {
  productId: string
  productName: string
  storageZone: 'frozen' | 'chilled' | 'ambient' | null
  totalQuantity: number
  unitType: string
  orderIds: string[]
  specialInstructions: string[]
  allergens: string[]
  allergenConflicts: AllergenConflict[]
  suggestedLot: { lotId: string; lotNumber: string; expirationDate: string | null } | null
}

export interface PickListResult {
  date: string
  pickupSlotId: string | null
  zones: {
    frozen: PickListLine[]
    chilled: PickListLine[]
    ambient: PickListLine[]
  }
  totalLines: number
}

export interface PackingSlipItem {
  productId: string
  productName: string
  unitType: string
  estimatedQuantity: number
  actualQuantity: number | null
  unitPriceCents: number
  allergens: string[]
}

export interface PackingSlipData {
  orderId: string
  orderNumber: string
  customerName: string
  customerEmail: string
  customerPhone: string | null
  customerAllergens: string[]
  pickupDate: string | null
  pickupSlot: string | null
  notes: string | null
  items: PackingSlipItem[]
  allergenWarnings: AllergenConflict[]
}

// ── Pick Lists & Packing Slips ──

export async function fetchPickList(date: string, pickupSlotId?: string) {
  const query = buildQuery({ date, pickup_slot_id: pickupSlotId })
  const res = await apiFetch<PickListResult>(`/api/v1/admin/pick-lists${query}`)
  return res.data ?? null
}

export async function fetchPackingSlip(orderId: string) {
  const res = await apiFetch<PackingSlipData>(`/api/v1/admin/orders/${orderId}/packing-slip`)
  return res.data ?? null
}

export async function fetchSlotPackingSlips(slotId: string, date: string) {
  const query = buildQuery({ date })
  const res = await apiFetch<PackingSlipData[]>(`/api/v1/admin/pickup-slots/${slotId}/packing-slips${query}`)
  return res.data ?? []
}

export async function checkOrderAllergens(orderId: string) {
  const res = await apiFetch<{ conflicts: AllergenConflict[]; hasConflicts: boolean }>(`/api/v1/admin/orders/${orderId}/allergen-check`)
  return res.data ?? { conflicts: [], hasConflicts: false }
}

// ── Morning Brief ──

export async function fetchMorningBrief(): Promise<MorningBriefData | null> {
  const brandId = getActiveBrand()
  const query = buildQuery({ brandId })
  const res = await apiFetch<MorningBriefData>(`/api/v1/admin/dashboard/morning-brief${query}`)
  return res.data ?? null
}

// ── Notifications ──

export async function fetchNotifications(): Promise<{ notifications: AdminNotification[]; unreadCount: number } | null> {
  const res = await apiFetch<{ notifications: AdminNotification[]; unreadCount: number }>('/api/v1/admin/notifications')
  return res.data ?? null
}

export async function markNotificationRead(id: string) {
  return apiFetch<{ success: boolean }>(`/api/v1/admin/notifications/${id}/read`, { method: 'PUT' })
}

export async function markAllNotificationsRead() {
  return apiFetch<{ success: boolean }>('/api/v1/admin/notifications/read-all', { method: 'PUT' })
}

// ── Global Search ──

export async function globalSearch(q: string): Promise<SearchResult | null> {
  if (!q || q.trim().length < 2) return null
  const brandId = getActiveBrand()
  const query = buildQuery({ q: q.trim(), brandId })
  const res = await apiFetch<SearchResult>(`/api/v1/admin/search${query}`)
  return res.data ?? null
}

// ── Bulk Order Actions ──

export interface BulkActionResult {
  results: Array<{ orderId: string; success: boolean; error?: string }>
  successCount: number
  failCount: number
}

export async function bulkOrderAction(orderIds: string[], action: 'confirm' | 'prepare' | 'ready' | 'picked_up' | 'cancel'): Promise<BulkActionResult | null> {
  const res = await apiFetch<BulkActionResult>('/api/v1/admin/orders/bulk-action', {
    method: 'POST',
    body: JSON.stringify({ orderIds, action }),
  })
  return res.data ?? null
}

// ── Inventory Lots ──

export interface LotRow {
  lot: {
    id: string
    productId: string
    lotNumber: string
    supplier?: string
    quantityReceived: number
    quantityRemaining: number
    unit: string
    receivedDate: string
    expirationDate: string | null
    status: string
    notes: string | null
    createdAt: string
  }
  productName: string
  productUnitLabel: string
}

export async function fetchLots(params?: { product_id?: string; status?: string }) {
  const query = buildQuery(params ?? {})
  const res = await apiFetch<LotRow[]>(`/api/v1/admin/inventory/lots${query}`)
  return res.data ?? []
}

export async function receiveLot(data: {
  productId: string
  lotNumber: string
  supplier?: string
  quantityReceived: number
  unitType: string
  expirationDate?: string
  notes?: string
}) {
  const res = await apiFetch<{ lotId: string }>('/api/v1/admin/inventory/lots', {
    method: 'POST',
    body: JSON.stringify(data),
  })
  return res.data ?? null
}

// ── Waste Logging ──

export interface WasteReportData {
  entries: Array<{
    entry: {
      id: string
      productId: string
      lotId: string | null
      quantity: number
      unit: string
      reason: string
      notes: string | null
      loggedBy: string
      loggedAt: string
    }
    productName: string
  }>
  byProduct: Array<{ productId: string; productName: string; totalQuantity: number; entries: number }>
  byReason: Array<{ reason: string; totalQuantity: number }>
  totalEntries: number
}

export async function logWaste(data: {
  productId: string
  lotId?: string
  quantity: number
  unitType: string
  reason: 'expired' | 'spoiled' | 'trim' | 'damaged' | 'other'
  notes?: string
}) {
  const res = await apiFetch<{ entryId: string }>('/api/v1/admin/inventory/waste-entries', {
    method: 'POST',
    body: JSON.stringify(data),
  })
  return res.data ?? null
}

export async function fetchWasteReport(range: '7d' | '30d' | '90d' = '30d') {
  const res = await apiFetch<WasteReportData>(`/api/v1/admin/inventory/waste-report?range=${range}`)
  return res.data ?? null
}

export interface ExpiringLot {
  id: string
  productId: string
  productName: string
  lotNumber: string
  quantityRemaining: number
  unit: string
  expirationDate: string
  daysUntilExpiry: number
}

export async function fetchExpiringLots(days = 7) {
  const res = await apiFetch<ExpiringLot[]>(`/api/v1/admin/inventory/expiring-soon?days=${days}`)
  return res.data ?? []
}

export interface LowStockAlert {
  inventory: {
    id: string
    productId: string
    tenantId: string
    currentQuantity: number
    unitType: string
    lowStockThreshold: number | null
    updatedAt: string
    version: number
  }
  productName: string
  productId: string
  brandId: string
}

export async function fetchLowStockAlerts() {
  const res = await apiFetch<LowStockAlert[]>('/api/v1/admin/inventory/low-stock')
  return res.data ?? []
}
